import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_EVENT } from "src/constants/mutations";
import { failureToast, successToast } from "src/utils/toasts";
import { UUID } from "src/types/shared.type";
import { RootState } from "src/store/index";
import { set_delete_modal_event_id } from "src/store/features/events-slice";
import { UpdateEventInput } from "src/interfaces/event.interface";
import { useAuth } from "src/contexts/auth-provider";

type UseDeleteEventOptions = {
  onDeleted?: () => void | Promise<void>;
};

export const useDeleteEvent = (options?: UseDeleteEventOptions) => {
  const dispatch = useDispatch();
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const onDeletedRef = useRef(options?.onDeleted);
  useEffect(() => {
    onDeletedRef.current = options?.onDeleted;
  }, [options?.onDeleted]);

  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const { toBeDeletedEventId } = useSelector((state: RootState) => state.events);

  const deleteEventMutation = useCallback(async () => {
    if (!toBeDeletedEventId || !hasRestaurant?.correlationId) {
      console.error("Cannot delete: No event ID or store correlation ID provided");
      return;
    }

    setIsDeletingEvent(true);
    try {
      const variables: { updateEventInput: UpdateEventInput } = {
        updateEventInput: {
          eventCorrelationId: toBeDeletedEventId,
          storeCorrelationId: hasRestaurant.correlationId,
          isActive: false,
        },
      };
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_EVENT,
        variables,
      });

      if (data?.data?.updateEvent) {
        dispatch(set_delete_modal_event_id(null));
        successToast("Event has been deleted successfully!");
        await onDeletedRef.current?.();
      }
    } catch (err) {
      console.error("Error deleting event:", err);
      failureToast("Failed to delete event");
    } finally {
      setIsDeletingEvent(false);
    }
  }, [axBe, dispatch, toBeDeletedEventId, hasRestaurant?.correlationId]);

  const handleOpenDeleteModal = useCallback(
    (correlationId: UUID) => {
      dispatch(set_delete_modal_event_id(correlationId));
    },
    [dispatch]
  );

  const handleCloseDeleteModal = useCallback(() => {
    dispatch(set_delete_modal_event_id(null));
  }, [dispatch]);

  return {
    isDeletingEvent,
    deleteEventMutation,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
  };
};
